// @flow
import $ from "../vendor/jquery";
import _ from "underscore";
// Global grobot objects
import {menu_translate, clear_old_page, recorrect_all_grid_rows} from "./Global";
import {activateMenuItem, asBoolean, asNumber, asString, getDecimal, getInt, htmlEncode, jsonAjax} from "../util";

import Wagons from "./Wagons";
import SchemaFactory from "../SchemaFactory";
import SchemaValidator from "../SchemaValidator";
import SchemaActions from "../actions/SchemaActions";
import SettingsActions from "../actions/SettingsActions";
import SchemaStore from "../stores/SchemaStore";
import type {IntegerType} from "../model";
import AppConfig from "../config";
import t, {getDecimalSep} from "../trans";
import Legacy from "../Legacy";


export type SettingsItems = {
    toleranceCompensation: boolean,
    filter: boolean,
    filterRepeatCount: boolean,
    filterCoef: IntegerType,
    filterMaxError: IntegerType,
    filter2: boolean,
    filter2RepeatCount: IntegerType,
    filter2Coef: IntegerType,
    filter2MaxError: IntegerType,
    lang: string,
    nominalGraph: boolean,
    mode: IntegerType,
    edgeSearch: boolean,
    ncpRotated: boolean,
    manualMeasuringRequested: boolean,
    manualMeasuringTooteKood: string,
    measuringInProgress: boolean,
    debugModeMeasuring: boolean,
    showOnlyCenterPoints: boolean,
    isGlobalCompensation: boolean,
    globalCompensation: IntegerType[],

    conf_debug: boolean,
    conf_max_records: IntegerType,              // Maximum number of records in archive search results
    conf_measurement_line_count: IntegerType,   // Measurement line count (3/5)
    conf_adas_area_count: IntegerType,          // ADAS area count (0..6)
    conf_wagon_count: IntegerType,              // Wagons count (24/34)
    conf_wagon_offset_loading: IntegerType,     // Wagon offset loading
    conf_wagon_offset_bending: IntegerType,     // Wagon offset bending
    conf_simple_ui: boolean,                    // Show simplified UI without some fields
    conf_no_wagons: boolean,                    // Measurements without wagons
    conf_manual_mode_allowed: boolean,          // Manual mode allowed
    conf_debug_mode_allowed: boolean,           // Debug mode allowed for measuring
    conf_complex_filter: boolean,               // PLC uses alternative filter parameters
    conf_glass_file_type: string,               // Supported glass file types, separated by comma (default is "dat,ncp")
    conf_rotated_measurement_allowed: boolean,  // Rotated measurement allowed
    conf_edge_search_allowed: boolean,          // Edge search allowed
    conf_slope_factor: number,                  // Slope factor for calculating slopes
    conf_center_add_points: IntegerType,
    conf_hud_points: IntegerType,
    conf_angle_points: IntegerType,
    conf_yshift_offset: IntegerType,
    conf_measurement_points: IntegerType,
    conf_raw_measurement_points: IntegerType,
    conf_log_level: string
};


type SettingsResult = {
    settingsResult: SettingsItems
};


/**
 * Seadete objekt.
 * Seaded on kasutaja poolt seadete lehel väärtustatavad parameetrid, mida hoitakse serveris meeles.
 *
 */
class Settings {
    /**
     * @type {?Settings}
     */
    static settingsInstance: ?Settings = null;

    settings_get_url: string;
    settings_save_url: string;
    values: SettingsItems;
    errors: Array<string>;
    schema: {};

    /**
     * @returns {Settings}
     */
    static getInstance(): Settings {
        if (Settings.settingsInstance == null) Settings.settingsInstance = new Settings();
        return Settings.settingsInstance;
    }

    constructor() {
        this.settings_get_url = AppConfig.BASE_URL + "/settings";
        this.settings_save_url = AppConfig.BASE_URL + "/settings";
        this.values = { 'toleranceCompensation': true };
        this.errors = [];
        let schemaFactory = SchemaFactory.getInstance();
        this.schema = schemaFactory.getSchema("settings");

        this.onSchemaChanged = this.onSchemaChanged.bind(this);
        SchemaStore.listen(this.onSchemaChanged);
    }

    onSchemaChanged(status) {
        if (status.schemas.settings != null) this.schema = status.schemas.settings;
    }

    getCompactedValues(): SettingsItems {
        // Skip fields with null value and skip configuration items (they are read-only)
        let allValues: SettingsItems = _.omit(this.values, function (v, k) {
            return v == null || (_.isNumber(v) && isNaN(v)) || v === "" || k.startsWith("conf_");
        });
        if (allValues.globalCompensation != null) {
            allValues.globalCompensation = [...allValues.globalCompensation];
            let nonZero = false;
            for (let v of allValues.globalCompensation) {
                if (v != null && v != 0) nonZero = true;
            }
            if (!nonZero) delete allValues.globalCompensation;
        }
        return allValues;
    }

    getLang(): string {
        return asString(this.values.lang, "en");
    }

    /**
     * Kas serveri konfiguratsioon on debug režiimis
     * @returns {boolean}
     */
    isDebug(): boolean {
        return asBoolean(this.values.conf_debug);
    }

    /**
     * Süsteemis kasutusel olevate vagunite arv
     * @returns {number}
     */
    getWagonCount(): IntegerType {
        let count = asNumber(this.values.conf_wagon_count);
        return (count == 0 ? 34 : count);
    }

    /**
     * Laadimises oleva vaguni numbri offset jooksva vaguni suhtes
     * @returns {number}
     */
    getWagonOffsetLoading(): IntegerType {
        let count = asNumber(this.values.conf_wagon_offset_loading);
        return (count == 0 ? 0 : count);
    }

    /**
     * Painutuses oleva vaguni numbri offset jooksva vaguni suhtes
     * @returns {number}
     */
    getWagonOffsetBending(): IntegerType {
        let count = asNumber(this.values.conf_wagon_offset_bending);
        return (count == 0 ? 0 : count);
    }

    /**
     *
     * @returns {boolean}
     */
    isNoWagons(): boolean {
        return asBoolean(this.values.conf_no_wagons);
    }

    /**
     *
     * @returns {number}
     */
    getMeasurementLineCount(): IntegerType {
        let count = asNumber(this.values.conf_measurement_line_count);
        if (count == 0) return 3;
        return count;
    }

    /**
     *
     * @returns {number}
     */
    getMeasurementPoints(): IntegerType {
        let count = asNumber(this.values.conf_measurement_points);
        if (count == 0) return 200;
        return count;
    }

    /**
     *
     * @returns {number}
     */
    getRawMeasurementPoints(): IntegerType {
        let count = asNumber(this.values.conf_raw_measurement_points);
        if (count == 0) return 1000;
        return count;
    }

    /**
     * Arhiivi poolt tagastatavate kirjete maks arv
     * @returns {number}
     */
    getMaxRecords(): IntegerType {
        let count = asNumber(this.values.conf_max_records);
        if (count == 0) return 100;
        return count;
    }

    /**
     * Kas tegemist on lihtsustatud kasutajaliidesega
     * @returns {boolean}
     */
    isSimpleUi(): boolean {
        return asBoolean(this.values.conf_simple_ui);
    }

    /**
     *
     * @returns {boolean}
     */
    isManualModeAllowed(): boolean {
        return asBoolean(this.values.conf_manual_mode_allowed, true);
    }

    /**
     *
     * @returns {boolean}
     */
    isDebugModeAllowed(): boolean {
        return asBoolean(this.values.conf_debug_mode_allowed);
    }

    /**
     *
     * @returns {boolean}
     */
    isRotatedMeasurementAllowed(): boolean {
        return asBoolean(this.values.conf_rotated_measurement_allowed);
    }

    /**
     *
     * @returns {boolean}
     */
    isEdgeSearchAllowed(): boolean {
        return asBoolean(this.values.conf_edge_search_allowed);
    }

    /**
     *
     * @returns {number}
     */
    getSlopeFactor(): number {
        let factor = asNumber(this.values.conf_slope_factor, 0.0);
        if (factor == 0.0) return 2.0;
        return factor;
    }

    /**
     *
     * @returns {number}
     */
    getCenterAddPoints(): IntegerType {
        let points = asNumber(this.values.conf_center_add_points);
        if (points < 0) points = 0;
        return points;
    }

    /**
     *
     * @returns {number}
     */
    getHudPoints(): IntegerType {
        let points = asNumber(this.values.conf_hud_points);
        if (points < 0) points = 0;
        return points;
    }

    /**
     * ADAS alade arv
     * @returns {IntegerType}
     */
    getAdasAreasCount(): IntegerType {
        let areas: IntegerType = asNumber(this.values.conf_adas_area_count);
        return (areas < 0 ? 0 : areas);
    }

    /**
     *
     * @returns {number}
     */
    getYshiftOffset(): number {
        let i = asNumber(this.values.conf_yshift_offset);
        // Vt ynihe
        if (i == null) i = 603 + 160;
        return i;
    }

    /**
     * Kas tuleb kuvada seadetes alternatiivne filtrite loogika
     *
     * @returns {boolean}
     */
    isComplexFilter(): boolean {
        return asBoolean(this.values.conf_complex_filter, true);
    }

    /**
     * Tagstab true, kui ollakse manuaalrežiimis
     * @returns {boolean}
     */
    isManualMode(): boolean {
        return this.getMode() == 1;
    }

    /**
     *
     * @returns {boolean}
     */
    isManualMeasuringRequested(): boolean {
        return asBoolean(this.values.manualMeasuringRequested);
    }

    /**
     *
     * @returns {string}
     */
    getManualMeasuringTooteKood(): string {
        return asString(this.values.manualMeasuringTooteKood, "");
    }

    /**
     *
     * @param kood
     */
    setManualMeasuringTooteKood(kood: string): void {
        this.values.manualMeasuringTooteKood = kood;
    }

    /**
     *
     * @returns {boolean}
     */
    isMeasuringInProgress(): boolean {
        return asBoolean(this.values.measuringInProgress);
    }

    /**
     *
     * @returns {boolean}
     */
    isToleranceCompensation(): boolean {
        if (this.isSimpleUi()) return false;
        return asBoolean(this.values.toleranceCompensation);
    }

    /**
     *
     * @returns {boolean}
     */
    isNcpRotated(): boolean {
        return asBoolean(this.values.ncpRotated);
    }

    /**
     *
     * @returns {boolean}
     */
    isEdgeSearch(): boolean {
        if (!this.isEdgeSearchAllowed()) return false;
        return asBoolean(this.values.edgeSearch);
    }

    /**
     *
     * @returns {boolean}
     */
    isDebugModeMeasuring(): boolean {
        return asBoolean(this.values.debugModeMeasuring);
    }

    /**
     *
     * @returns {boolean}
     */
    showOnlyCenterPoints(): boolean {
        if (this.isSimpleUi()) return false;
        return asBoolean(this.values.showOnlyCenterPoints);
    }

    /**
     *
     * @returns {number}
     */
    getMode(): IntegerType {
        return asNumber(this.values.mode);
    }

    /**
     *
     * @param {number} mode
     */
    setMode(mode: IntegerType): void {
        this.values.mode = mode;
        this.save_settings();
    }

    getNominalGraph(): boolean {
        return asBoolean(this.values.nominalGraph);
    }

    /**
     *
     * @returns {string}
     */
    getGlassFileType(): string {
        return asString(this.values.conf_glass_file_type, "dat,ncp");
    }

    isGlobalCompensation(): boolean {
        return asBoolean(this.values.isGlobalCompensation);
    }

    setIsGlobalCompensation(isGlobalCompensation: boolean): void {
        this.values.isGlobalCompensation = isGlobalCompensation;
    }

    getGlobalCompensation(lineNo: IntegerType): number {
        let c: IntegerType[] = this.values.globalCompensation;
        return (c == null || c.length < lineNo ? 0 : c[lineNo]);
    }

    setGlobalCompensation(lineNo: IntegerType, value: number): void {
        if (this.values.globalCompensation == null)
            this.values.globalCompensation = [];
        this.values.globalCompensation[lineNo] = value;
    }

    setLang(lang: string): void {
        this.values.lang = lang;
    }

    /**
     * Settings lehe aktiveerimine ja kuvamine
     */
    make_settings_page() {
        menu_translate();
        clear_old_page();

        let s = '<form onsubmit="return false;" class="form-horizontal" style="padding-left: 10px;">';
        s += '<div id="errors"></div>';

        s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><div class="checkbox"><label>';
        s += '<input type="checkbox" id="nominalGraph"> ';
        s += htmlEncode(t("Show nominal graph")) + '</label></div></div></div>\n';

        if (!this.isSimpleUi()) {
            s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><div class="checkbox"><label>';
            s += '<input type="checkbox" id="showOnlyCenterPoints"> ';
            s += htmlEncode(t("Show only center points")) + '</label></div></div></div>\n';

            s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><div class="checkbox"><label>';
            s += '<input type="checkbox" id="toleranceCompensation"> ';
            s += htmlEncode(t("Tolerance compensation")) + '</label></div></div></div>\n';
        }

        if (this.isComplexFilter()) {
            s += '<div class="panel panel-default"><div class="panel-body">';	// Panel start
            s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><div class="checkbox"><label>';
            s += '<input type="checkbox" id="filter">';
            s += htmlEncode(t("Use filter 1")) + '</label></div></div></div>\n';

            s += '<div class="form-group"><label for="filterRepeatCount" class="col-xs-2 control-label">';
            s += htmlEncode(t("Filter 1 windows width")) + '</label>';
            s += '<div class="col-xs-4"><input type="number" class="form-control" id="filterRepeatCount" min="5" max="20"> ';
            s += '(' + htmlEncode(this.getSchemaRestriction(this.schema.properties.filterRepeatCount)) + ')';
            s += '</div></div>';

            s += '<div class="form-group"><label for="filterCoef" class="col-xs-2 control-label">';
            s += htmlEncode(t("Filter 1 maximum error")) + '</label>';
            s += '<div class="col-xs-4"><input type="number" class="form-control" id="filterCoef" min="0.2" max="2.0">';
            s += '(' + htmlEncode(this.getSchemaRestriction(this.schema.properties.filterCoef, 100, 1)) + ')';
            s += '</div></div>';

            s += '<div class="form-group"><label for="filterMaxError" class="col-xs-2 control-label">';
            s += htmlEncode(t("Filter 1 ignored edge")) + '</label>';
            s += '<div class="col-xs-4"><input type="number" class="form-control" id="filterMaxError" min="1" max="20">';
            s += '(' + htmlEncode(this.getSchemaRestriction(this.schema.properties.filterMaxError)) + ')';
            s += '</div></div>';
            s += '</div></div>';	// Panel end

            s += '<div class="panel panel-default"><div class="panel-body">';	// Panel start
            s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><div class="checkbox"><label>';
            s += '<input type="checkbox" id="filter2">';
            s += htmlEncode(t("Use filter 2")) + '</label></div></div></div>\n';

            s += '<div class="form-group"><label for="filter2RepeatCount" class="col-xs-2 control-label">';
            s += htmlEncode(t("Filter 2 windows width")) + '</label>';
            s += '<div class="col-xs-4"><input type="number" class="form-control" id="filter2RepeatCount" min="5" max="20"> ';
            s += '(' + htmlEncode(htmlEncode(this.getSchemaRestriction(this.schema.properties.filter2RepeatCount))) + ')';
            s += '</div></div>';

            s += '<div class="form-group"><label for="filter2Coef" class="col-xs-2 control-label">';
            s += htmlEncode(t("Filter 2 maximum error")) + '</label>';
            s += '<div class="col-xs-4"><input type="number" class="form-control" id="filter2Coef" min="0.2" max="2.0">';
            s += '(' + htmlEncode(this.getSchemaRestriction(this.schema.properties.filter2Coef, 100, 1)) + ')';
            s += '</div></div>';

            s += '<div class="form-group"><label for="filter2MaxError" class="col-xs-2 control-label">';
            s += htmlEncode(t("Filter 2 ignored edge")) + '</label>';
            s += '<div class="col-xs-4"><input type="number" class="form-control" id="filter2MaxError" min="1" max="20">';
            s += '(' + htmlEncode(this.getSchemaRestriction(this.schema.properties.filter2MaxError)) + ')';
            s += '</div></div>';
            s += '</div></div>';	// Panel end

        } else {
            s += '<div class="panel panel-default"><div class="panel-body">';	// Panel start
            s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><div class="checkbox"><label>';
            s += '<input type="checkbox" id="filter">';
            s += htmlEncode(t("Use filter")) + '</label></div></div></div>\n';

            s += '<div class="form-group"><label for="filterRepeatCount" class="col-xs-2 control-label">';
            s += htmlEncode(t("Filter repeat count")) + '</label>';
            s += '<div class="col-xs-4"><input type="number" class="form-control" id="filterRepeatCount" min="1" max="5"> ';
            s += '(' + htmlEncode(this.getSchemaRestriction(this.schema.properties.filterRepeatCount)) + ')';
            s += '</div></div>';

            s += '<div class="form-group"><label for="filterCoef" class="col-xs-2 control-label">';
            s += htmlEncode(t("Filter coefficient")) + '</label>';
            s += '<div class="col-xs-4"><input type="number" class="form-control" id="filterCoef" min="0" max="300">';
            s += '(' + htmlEncode(this.getSchemaRestriction(this.schema.properties.filterCoef, 100)) + ')';
            s += '</div></div>';

            s += '<div class="form-group"><label for="filterMaxError" class="col-xs-2 control-label">';
            s += htmlEncode(t("Filter max permissible number of deviations")) + '</label>';
            s += '<div class="col-xs-4"><input type="number" class="form-control" id="filterMaxError" min="0" max="200">';
            s += '(' + htmlEncode(this.getSchemaRestriction(this.schema.properties.filterMaxError)) + ')';
            s += '</div></div>';
            s += '</div></div>';	// Panel end

        }

        s += '<div class="panel panel-default"><div class="panel-body">';	// Panel start
        s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><div class="checkbox"><label>';
        s += '<input type="checkbox" id="isGlobalCompensation">';
        s += htmlEncode(t("Use global glass thickness compensation")) + '</label></div></div></div>\n';

        for (let line = 0; line < this.getMeasurementLineCount(); line++) {
            s += '<div class="form-group"><label for="globalCompensation['+line+']" class="col-xs-2 control-label">';
            s += htmlEncode(t("Compensation for line") + " " + (line+1)) + '</label>';
            s += '<div class="col-xs-4"><input type="number" class="form-control" id="globalCompensation_'+line+'"> ';
            s += '(mm, ' + htmlEncode(this.getSchemaRestriction(this.schema.properties.globalCompensation.items, 10, 1)) + ')';
            s += '</div></div>';
        }
        s += '</div></div>';	// Panel end


        s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><div class="checkbox"><label>';
        s += '<input type="checkbox" id="ncpRotated"> ';
        s += htmlEncode(t("NCP file is rotated")) + '</label></div></div></div>\n';

        if (this.isManualModeAllowed()) {
            s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><div class="checkbox"><label>';
            s += '<input type="checkbox" id="mode"> ';
            s += htmlEncode(t("Manual measuring mode")) + '</label></div></div></div>\n';

            if (this.isEdgeSearchAllowed()) {
                s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><div class="checkbox"><label>';
                s += '<input type="checkbox" id="edgeSearch"> ';
                s += htmlEncode(t("Edge search")) + '</label></div></div></div>\n';
            }
        }

        if (this.isDebugModeAllowed()) {
            s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><div class="checkbox"><label>';
            s += '<input type="checkbox" id="debugModeMeasuring"> ';
            s += htmlEncode(t("Debug mode for measuring")) + '</label></div></div></div>\n';
        }

        s += '<div class="form-group"><div class="col-xs-offset-2 col-xs-4"><button type="button" class="btn btn-primary" onclick="settings.save_settings_form()">';
        s += htmlEncode(t('Save'));
        s += '</button></div></div>';

        s += "</form><br>";
        s += '<p class="bg-success" id="success">' + htmlEncode(t('Settings saved')) + '</p>';

        $("#topy").css("height", "50px");
        $("#content").html(s);
        this.load_settings_form();

        activateMenuItem("settings");
    }

    getSchemaRestriction(schema: {}, koef: ?IntegerType = null, decimals: ?IntegerType = null): string {
        if (schema.minimum != null && schema.maximum != null) {
            return t("between") + " " +
                this.formatNumber(koef ? schema.minimum / koef : schema.minimum, decimals) + ".." +
                this.formatNumber(koef ? schema.maximum / koef : schema.maximum, decimals);
        }
        if (schema.minimum != null) {
            return t("over") + " " + this.formatNumber(koef ? schema.minimum / koef : schema.minimum, decimals);
        }
        if (schema.maximum != null) {
            return t("below") + " " + this.formatNumber(koef ? schema.minimum / koef : schema.minimum, decimals);
        }
        return "";
    }

    formatNumber(value: number, decimals: IntegerType): string {
        if (decimals == null) return value.toString();

        let strvalue = value.toFixed(decimals);
        let sep = getDecimalSep();
        return strvalue.replace(".", sep);
    }

    /**
     * Seadete hashi baasilt seadete vormi väätustamine. Eeldab, et seadete vorm on ekraanil.
     *
     * Arvestama peab, et teatud seaded on meil sisemiselt skaleeritud üles:
     *    * filterCoef - 100x
     *    * filter2Coef - 100x
     *    * globalCompensation - 10x
     */
    load_settings_form() {
        $('#nominalGraph').attr('checked', this.getNominalGraph());
        $('#showOnlyCenterPoints').attr('checked', this.showOnlyCenterPoints());
        $('#toleranceCompensation').attr('checked', asBoolean(this.values.toleranceCompensation));

        $('#filter').attr('checked', asBoolean(this.values.filter));
        $('#filterRepeatCount').val(asString(this.values.filterRepeatCount));
        let v = asNumber(this.values.filterCoef, null);
        if (v != null) v /= 100;
        $('#filterCoef').val(asString(v));
        $('#filterMaxError').val(asString(this.values.filterMaxError));

        $('#filter2').attr('checked', asBoolean(this.values.filter2));
        $('#filter2RepeatCount').val(asString(this.values.filter2RepeatCount));
        v = asNumber(this.values.filter2Coef, null);
        if (v != null) v /= 100;
        $('#filter2Coef').val(asString(v));
        $('#filter2MaxError').val(asString(this.values.filter2MaxError));

        $('#isGlobalCompensation').attr('checked', this.isGlobalCompensation());
        for (let line = 0; line < this.getMeasurementLineCount(); line++) {
            $('#globalCompensation_'+line).val(this.getGlobalCompensation(line) / 10);
        }

        $('#ncpRotated').attr('checked', this.isNcpRotated());
        $('#debugModeMeasuring').attr('checked', this.isDebugModeMeasuring());
        $('#mode').attr('checked', this.getMode() == 1);
        if (this.getMode() == 1) $('#edgeSearch').attr('checked', this.isEdgeSearch());
        $('#success').hide();

    }

    /**
     * Seadete vormi sisu kandmine seadete hashi ning seadete salvestamine. Salvestamise õnnestumisel kuvatakse vastav teade.
     */
    save_settings_form() {
        this.values.nominalGraph = !!$('#nominalGraph').is(':checked');
        this.values.showOnlyCenterPoints = !!$('#showOnlyCenterPoints').is(':checked');
        let oldToleranceCompensation = this.values.toleranceCompensation;
        this.values.toleranceCompensation = !!$('#toleranceCompensation').is(':checked');
        this.values.filter = !!$('#filter').is(':checked');
        this.values.filterRepeatCount = getInt($('#filterRepeatCount').val());
        let v = getDecimal($('#filterCoef').val());
        if (v != null) v = Math.round(v*100);
        this.values.filterCoef = v;
        this.values.filterMaxError = getInt($('#filterMaxError').val());
        this.values.filter2 = !!$('#filter2').is(':checked');
        this.values.filter2RepeatCount = getInt($('#filter2RepeatCount').val());
        v = getDecimal($('#filter2Coef').val());
        if (v != null) v = Math.round(v*100);
        this.values.filter2Coef = v;
        this.values.filter2MaxError = getInt($('#filter2MaxError').val());

        this.setIsGlobalCompensation(!!$('#isGlobalCompensation').is(':checked'));
        for (let line = 0; line < this.getMeasurementLineCount(); line++) {
            let v = getDecimal($('#globalCompensation_'+line).val());
            if (v != null) v = Math.round(v*10);
            else v = 0;
            this.setGlobalCompensation(line, v);
        }

        this.values.ncpRotated = !!$('#ncpRotated').is(':checked');
        this.values.mode = $('#mode').is(':checked') ? 1 : 0;
        this.values.edgeSearch = !!$('#edgeSearch').is(':checked');
        this.values.debugModeMeasuring = !!$('#debugModeMeasuring').is(':checked');
        if (this.values.toleranceCompensation != oldToleranceCompensation) recorrect_all_grid_rows();
        if (this.validateSettings()) this.save_settings();
        else $('#success').hide();

    }

    /**
     *
     * @param {?function} callback
     */
    load_settings(callback, errorCallback) {
        let wurl = this.settings_get_url;
        Legacy.debug("loading settings " + wurl);
        jsonAjax({
            type: "GET",
            url: wurl,
            success: function (data: SettingsResult) {
                this.settingscall(data);
                if (Object.keys(this.schema).length === 0) SchemaActions.fetchSchema("settings");
                if (callback) callback();
            }.bind(this),
            error: function () {
                if (errorCallback) errorCallback();
            }
        });
    }

    save_settings() {
        let u = this.settings_save_url;
        let r = { data: this.getCompactedValues() };
        let s = JSON.stringify(r);
        Legacy.debug("Settings data: " + s);
        jsonAjax({
            type: "POST",
            url: u,
            data: r,
            success: function() {
                this.save_settings_success();
            }.bind(this),
            /*error: function (a,b,c) {
            	show_err(t("Error saving settings"));
            }*/
        });
        return false;
    }

    save_settings_success() {
        this.load_settings();
        $('#success').show();
    }

    validateSettings() {
        // Internally the following fields are scaled up:
        // filterCoef - 100x
        // filter2Coef - 100x
        // globalCompensation[i] - 10x
        let allSettings = this.getCompactedValues();

        let validator = new SchemaValidator(this.schema);
        if (!validator.validate(allSettings)) {
            // Errors, do not save:
            this.errors = validator.errors();
        } else {
            this.errors = [];
        }
        this.showErrors();
        return this.errors.length == 0;
    }

    showErrors() {
        $("#errors").empty();
        if (this.errors && this.errors.length > 0) {
            $("#errors").append(`<div class="row"><div class="col-md-12 text-danger">${htmlEncode(t("Some fields contain invalid values"))}:</div></div>`);
            for (let e of this.errors) {
                $("#errors").append(`<div class="row"><div class="col-md-11 col-md-offset-1 text-danger">${htmlEncode("'" + e.schema.title + "' " + e.message)}</div></div>`);
            }
        }
    }

    /**
     *
     * @param {object} data
     */
    settingscall(data: ?SettingsResult) {
        if (data == null) return;
        let r: SettingsItems = data.settingsResult;
        if (r == null) return;

        // If no changes, then return:
        if (_.isMatch(this.values, r)) return;

        // Nullime vanad seadingud:
        let oldValues = this.values;
        this.values = r;

        // TODO: Testida, et miks tolernaceCompensation settingute lugemisel vaikimisi sisse lülitatakse
        //this.set('toleranceCompensation', true);
        if (oldValues.toleranceCompensation != this.values.toleranceCompensation) recorrect_all_grid_rows();

        if (this.isManualMode()) {
            // TODO: Get rid of calling Wagons from Settings!
            let wagons = Wagons.getInstance();
            wagons.make_manual_mode_bar();
            wagons.restore_manual_mode_bar();
            wagons.init_manual_mode_bar();
        }
        // Et GrobotSettings store saaks uuemad seadingud
        SettingsActions.settingsUpdated(this.values);
    }

    get_manual_measuring() {
        jsonAjax({
            type: 'GET',
            url: AppConfig.BASE_URL + '/manualMeasuring',
            success: function (data: ManualMeasuringResultType) {
                let r = data.manualMeasuringResult;
                this.values.mode = r.mode;
                // TODO: Get rid of calling Wagons from Settings!
                let wagons = Wagons.getInstance();
                if (r.measuringRequested > 0 || r.measuringInProgress > 0)
                    wagons.measuring_started(r.tooteKood);
                else
                    wagons.measuring_finished();
            }.bind(this)

        });
    }

    reset_horn() {
        jsonAjax({
            type: 'POST',
            url: AppConfig.BASE_URL + '/hornReset',
            data: {reset: true},
            success: function () {
                // Nothing to do
            }
        });
    }
}

export default Settings;
